import { createSlice } from "@reduxjs/toolkit";
import axiosInstanceWithoutInterceptor from "../axiosWithoutInterceptor";
import toast from "../hooks/toast";
import { deduplicateRequest } from "../shared/services/apiDeduplicationService";
import { buildApiV3Url, buildApiV3UrlWithQuery } from "../shared/Utils";

// Module-level promise to track in-flight request for company groups by user
let companyGroupsByUserPromise = null;

const escapeODataString = value => value.replace(/'/g, "''");

const [showToast] = toast();
const initialState = {
    isLoading: false,
    companies: [],
    isLoadingCompanies: false,
    selectedGroup: null,
    isLoadingGroup: false,
    page: {
        data: [],
        paginate: {
            pageNumber: 0,
            startIndex: -1,
            endIndex: 0,
            rowsPerPage: 0,
            totalRecords: 0,
        },
    },
    formState: {
        nameOrCode: "",
    },
    sortColumn: ["name", true],
    companyGroups: [],
    companyGroupHierarchyData: [],
    isLoadingCompanyGroupHierarchy: false,
    companyGroupHierarchyCount: 0,
    companyGroupHierarchyPage: 1,
    companyGroupHierarchyPageSize: 10,
    allGroupsForMove: [],
    isLoadingAllGroupsForMove: false,
    companyGroupHierarchyError: false,
};

export const selectIsLoading = state => {
    return state.companyGroups.isLoading;
};
export const selectPage = state => {
    return state.companyGroups.page;
};
export const selectFormState = state => {
    return state.companyGroups.formState;
};
export const selectSortColumn = state => {
    return state.companyGroups.sortColumn;
};
export const selectCompanyGroups = state => {
    return state.companyGroups.companyGroups;
};
export const selectIsLoadingCompanies = state => {
    return state.companyGroups.isLoadingCompanies;
};
export const selectCompanies = state => {
    return state.companyGroups.companies;
};
export const selectSelectedGroup = state => {
    return state.companyGroups.selectedGroup;
};
export const selectIsLoadingGroup = state => {
    return state.companyGroups.isLoadingGroup;
};
export const selectCompanyGroupHierarchyData = state => {
    return state.companyGroups.companyGroupHierarchyData;
};
export const selectIsLoadingCompanyGroupHierarchy = state => {
    return state.companyGroups.isLoadingCompanyGroupHierarchy;
};
export const selectCompanyGroupHierarchyCount = state => {
    return state.companyGroups.companyGroupHierarchyCount;
};
export const selectCompanyGroupHierarchyPage = state => {
    return state.companyGroups.companyGroupHierarchyPage;
};
export const selectCompanyGroupHierarchyPageSize = state => {
    return state.companyGroups.companyGroupHierarchyPageSize;
};
export const selectAllGroupsForMove = state => {
    return state.companyGroups.allGroupsForMove;
};
export const selectIsLoadingAllGroupsForMove = state => {
    return state.companyGroups.isLoadingAllGroupsForMove;
};
export const selectCompanyGroupHierarchyError = state => {
    return state.companyGroups.companyGroupHierarchyError;
};

/**
 * Returns the server-filtered company group hierarchy data. Filtering is handled server-side
 * via the nameOrCode filter param in fetchCompanyGroupHierarchy.
 */
export const selectFilteredCompanyGroupHierarchyData = state =>
    state.companyGroups.companyGroupHierarchyData;
export const companyGroupsSlice = createSlice({
    name: "companyGroups",
    initialState,
    reducers: {
        setIsLoading: (state, action) => {
            state.isLoading = action.payload;
        },
        setPage: (state, action) => {
            state.page = action.payload;
        },
        setFormState: (state, action) => {
            state.formState = action.payload;
        },
        setSortColumn: (state, action) => {
            state.sortColumn = action.payload;
        },
        setCompanyGroups: (state, action) => {
            state.companyGroups = action.payload;
        },
        clearCompanyGroupsSearch: state => {
            state.formState = initialState.formState;
        },
        setIsLoadingCompanies: (state, action) => {
            state.isLoadingCompanies = action.payload;
        },
        setCompanies: (state, action) => {
            state.companies = action.payload;
        },
        setLoadingGroup: (state, action) => {
            state.isLoadingGroup = action.payload;
        },
        setSelectedGroup: (state, action) => {
            state.selectedGroup = action.payload;
        },
        setPageToNull: state => {
            state.page = {
                data: [],
                paginate: {
                    pageNumber: 0,
                    startIndex: -1,
                    endIndex: 0,
                    rowsPerPage: 0,
                    totalRecords: 0,
                },
            };
        },
        setCompanyGroupHierarchyData: (state, action) => {
            state.companyGroupHierarchyData = action.payload;
        },
        setIsLoadingCompanyGroupHierarchy: (state, action) => {
            state.isLoadingCompanyGroupHierarchy = action.payload;
        },
        setCompanyGroupHierarchyCount: (state, action) => {
            state.companyGroupHierarchyCount = action.payload;
        },
        setCompanyGroupHierarchyPage: (state, action) => {
            state.companyGroupHierarchyPage = action.payload;
        },
        setCompanyGroupHierarchyPageSize: (state, action) => {
            state.companyGroupHierarchyPageSize = action.payload;
        },
        setAllGroupsForMove: (state, action) => {
            state.allGroupsForMove = action.payload;
        },
        setIsLoadingAllGroupsForMove: (state, action) => {
            state.isLoadingAllGroupsForMove = action.payload;
        },
        setCompanyGroupHierarchyError: (state, action) => {
            state.companyGroupHierarchyError = action.payload;
        },
    },
});
export const {
    setIsLoading,
    setCompanies,
    setIsLoadingCompanies,
    setPage,
    setPageToNull,
    setFormState,
    setSortColumn,
    setCompanyGroups,
    clearCompanyGroupsSearch,
    setLoadingGroup,
    setSelectedGroup,
    setCompanyGroupHierarchyData,
    setIsLoadingCompanyGroupHierarchy,
    setCompanyGroupHierarchyCount,
    setCompanyGroupHierarchyPage,
    setCompanyGroupHierarchyPageSize,
    setAllGroupsForMove,
    setIsLoadingAllGroupsForMove,
    setCompanyGroupHierarchyError,
} = companyGroupsSlice.actions;

const parseCompanyGroupHierarchyResponse = data => {
    if (Array.isArray(data)) {
        return { companyGroupHierarchyNodes: data, count: data.length };
    }
    if (data?.value) {
        const companyGroupHierarchyNodes = data.value;
        const count =
            data["@recordsetCount"] ??
            data.recordsetCount ??
            data.count ??
            companyGroupHierarchyNodes.length;
        return { companyGroupHierarchyNodes, count };
    }
    return { companyGroupHierarchyNodes: [], count: 0 };
};

export const fetchAllGroupsForMove = () => async (dispatch, getState) => {
    const { activeCompany } = getState().session;
    const PAGE_SIZE = 200; // Max page size is 200 (assuming more than this groups will not be present)
    dispatch(setIsLoadingAllGroupsForMove(true));
    try {
        const response = await axiosInstanceWithoutInterceptor.get(
            buildApiV3UrlWithQuery(
                `companies/${activeCompany.id}/company-divisions`,
                "",
                "",
                PAGE_SIZE
            ),
            { withCredentials: true }
        );
        const groups = response?.data?.value ?? [];
        dispatch(setAllGroupsForMove(groups));
    } catch {
        dispatch(setAllGroupsForMove([]));
        showToast("error", "Failed to load groups for move");
    } finally {
        dispatch(setIsLoadingAllGroupsForMove(false));
    }
};

export const fetchCompanyGroupHierarchy =
    (params = {}) =>
    async (dispatch, getState) => {
        const { activeCompany } = getState().session;
        const { companyGroupHierarchyPage, companyGroupHierarchyPageSize, formState } =
            getState().companyGroups;
        const page = params.page ?? companyGroupHierarchyPage;
        const pageSize = params.pageSize ?? companyGroupHierarchyPageSize;
        const top = pageSize;
        const skip = (page - 1) * pageSize;
        const nameOrCode = formState?.nameOrCode?.trim();
        const filter = nameOrCode ? `name like '${escapeODataString(nameOrCode)}'` : "";

        dispatch(setIsLoadingCompanyGroupHierarchy(true));
        try {
            const response = await axiosInstanceWithoutInterceptor.get(
                buildApiV3UrlWithQuery(
                    `companies/${activeCompany.id}/company-divisions/hierarchy`,
                    filter,
                    "",
                    top,
                    skip
                ),
                { withCredentials: true }
            );
            const { companyGroupHierarchyNodes, count } = parseCompanyGroupHierarchyResponse(
                response?.data
            );
            dispatch(setCompanyGroupHierarchyData(companyGroupHierarchyNodes));
            dispatch(setCompanyGroupHierarchyCount(count));
            dispatch(setCompanyGroupHierarchyError(false));
        } catch {
            dispatch(setCompanyGroupHierarchyData([]));
            dispatch(setCompanyGroupHierarchyCount(0));
            dispatch(setCompanyGroupHierarchyError(true));
            showToast("error", "Failed to load company groups");
        } finally {
            dispatch(setIsLoadingCompanyGroupHierarchy(false));
        }
    };

export const moveGroup =
    (groupId, targetParentGroupId, { silent = false } = {}) =>
    async (dispatch, getState) => {
        const { activeCompany } = getState().session;

        try {
            const response = await axiosInstanceWithoutInterceptor.put(
                buildApiV3UrlWithQuery(
                    `companies/${activeCompany.id}/company-divisions/${groupId}/parent`
                ),
                { targetParentGroupId: targetParentGroupId ?? null },
                { withCredentials: true }
            );

            if (response.status === 200) {
                if (!silent) {
                    showToast("success", "Company group moved successfully");
                    dispatch(setCompanyGroupHierarchyPage(1));
                    dispatch(fetchCompanyGroupHierarchy({ page: 1 }));
                }
                return true;
            }

            if (!silent) {
                showToast("error", response.message ?? "Failed to move company group");
            }
            return false;
        } catch {
            if (!silent) {
                showToast("error", "Failed to move company group");
            }
            return false;
        }
    };

export const moveCompany =
    (clientEntityId, targetGroupId, { silent = false } = {}) =>
    async (dispatch, getState) => {
        const { activeCompany } = getState().session;
        try {
            const response = await axiosInstanceWithoutInterceptor.put(
                buildApiV3UrlWithQuery(
                    `companies/${activeCompany.id}/company-divisions/companies/${clientEntityId}/group`
                ),
                { targetGroupId: targetGroupId ?? null },
                { withCredentials: true }
            );
            if (response.status === 200) {
                if (!silent) {
                    const message =
                        targetGroupId == null
                            ? "Company removed from group"
                            : "Company moved successfully";
                    showToast("success", message);
                    dispatch(setCompanyGroupHierarchyPage(1));
                    dispatch(fetchCompanyGroupHierarchy({ page: 1 }));
                }
                return true;
            }
            if (!silent) {
                showToast("error", response.message ?? "Failed to move company");
            }
            return false;
        } catch (error) {
            if (!silent) {
                showToast("error", error?.response?.data?.message ?? "Failed to move company");
            }
            return false;
        }
    };

export const createCompanyGroup = payload => async (dispatch, getState) => {
    dispatch(setIsLoading(true));
    const { activeCompany } = getState().session;
    try {
        const response = await axiosInstanceWithoutInterceptor.post(
            buildApiV3UrlWithQuery(`companies/${activeCompany.id}/company-divisions`),
            payload,
            {
                withCredentials: true,
            }
        );
        if (response.status === 200) {
            showToast("success", "Your company group has been created");
            dispatch(fetchCompanyGroupHierarchy());
            return true;
        }
        return false;
    } catch (error) {
        const message =
            error?.response?.data?.message ??
            error?.response?.data?.detail ??
            "Failed to create company group";
        showToast("error", message);
        return false;
    } finally {
        dispatch(setIsLoading(false));
    }
};

export const updateCompanyGroup = (groupId, payload) => async (dispatch, getState) => {
    dispatch(setIsLoading(true));
    const { activeCompany } = getState().session;
    try {
        const response = await axiosInstanceWithoutInterceptor.put(
            buildApiV3UrlWithQuery(`companies/${activeCompany.id}/company-divisions/${groupId}`),
            payload,
            {
                withCredentials: true,
            }
        );
        if (response.status === 200) {
            showToast("success", "Your company group has been updated");
            dispatch(fetchCompanyGroupHierarchy());
            return true;
        }
        return false;
    } catch (error) {
        const message =
            error?.response?.data?.message ??
            error?.response?.data?.detail ??
            "Failed to update company group";
        showToast("error", message);
        return false;
    } finally {
        dispatch(setIsLoading(false));
    }
};

export const fetchCompanies = () => async (dispatch, getState) => {
    const { activeCompany } = getState().session;
    const { isLoadingCompanies } = getState().companyGroups;
    if (isLoadingCompanies) {
        return;
    }
    dispatch(setIsLoadingCompanies(true));

    try {
        const response = await axiosInstanceWithoutInterceptor.get(
            buildApiV3UrlWithQuery(`companies/${activeCompany.id}/company-divisions/mappings`),
            {
                withCredentials: true,
            }
        );

        const companiesById = new Map();
        for (const company of response.data) {
            const companyId = Number(company.companyId);
            const entry = {
                label: company.name,
                value: String(company.companyId),
                companyId: company.companyId,
                isDivisionMapped: company.isDivisionMapped,
            };
            const existing = companiesById.get(companyId);
            if (!existing || (!existing.isDivisionMapped && company.isDivisionMapped)) {
                companiesById.set(companyId, entry);
            }
        }

        dispatch(setCompanies(Array.from(companiesById.values())));
    } catch {
        dispatch(setCompanies([]));
        showToast("error", "Failed to load companies");
    } finally {
        dispatch(setIsLoadingCompanies(false));
    }
};

export const deleteCompanyGroup =
    (groupId, { silent = false } = {}) =>
    async (dispatch, getState) => {
        const { activeCompany } = getState().session;

        try {
            const response = await axiosInstanceWithoutInterceptor.delete(
                buildApiV3Url(`companies/${activeCompany.id}/company-divisions/${groupId}`),
                {
                    withCredentials: true,
                }
            );

            if (response.status === 204) {
                if (!silent) {
                    dispatch(setCompanyGroupHierarchyPage(1));
                    dispatch(fetchCompanyGroupHierarchy({ page: 1 }));
                    showToast("success", "Your company group has been deleted");
                }
                return true;
            }

            if (!silent) {
                showToast("error", response.message);
            }
            return false;
        } catch {
            if (!silent) {
                showToast("error", "Failed to delete company group");
            }
            return false;
        }
    };

export const fetchCompanyGroupById = groupId => async (dispatch, getState) => {
    const { isLoadingGroup } = getState().companyGroups;
    if (isLoadingGroup) {
        return;
    }
    dispatch(setLoadingGroup(true));
    const { activeCompany } = getState().session;

    try {
        const response = await axiosInstanceWithoutInterceptor.get(
            buildApiV3UrlWithQuery(`companies/${activeCompany.id}/company-divisions/${groupId}`),
            {
                withCredentials: true,
            }
        );
        if (response?.data) {
            const groupData = {
                name: response.data.name,
                companies: response.data.companies
                    ? response.data.companies.map(company => ({
                          label: company.name,
                          value: String(company.companyId),
                          companyId: company.companyId,
                      }))
                    : [],
                groupId: response.data.groupId,
                parentGroupId: response.data.parentGroupId ?? null,
            };
            dispatch(setSelectedGroup(groupData));
        }
    } catch {
        showToast("error", "Failed to load company group details");
    } finally {
        dispatch(setLoadingGroup(false));
    }
};

export const getCompanyGroupsByUser = async (dispatch, getState) => {
    const localState = getState();
    const { companyGroups } = localState.companyGroups;
    const { activeCompany } = localState.session;

    // Return cached data if already loaded
    if (companyGroups && companyGroups.length > 0) {
        return true;
    }

    // Use deduplication to prevent concurrent duplicate calls
    companyGroupsByUserPromise = deduplicateRequest(companyGroupsByUserPromise, async () => {
        dispatch(setLoadingGroup(true));
        const apiURI = buildApiV3UrlWithQuery(
            `companies/${activeCompany.id}/company-divisions/user`
        );
        return axiosInstanceWithoutInterceptor.get(`${apiURI}`, {
            withCredentials: true,
        });
    });

    try {
        const response = await companyGroupsByUserPromise;

        if (response?.data) {
            dispatch(setCompanyGroups(response.data));
        } else {
            dispatch(setCompanyGroups([]));
        }
        return true;
    } catch {
        dispatch(setCompanyGroups([]));
        return false;
    } finally {
        companyGroupsByUserPromise = null;
        dispatch(setLoadingGroup(false));
    }
};

export default companyGroupsSlice.reducer;
